import type { Id } from "@repo/convex/dataModel";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MessageRichTextSubmitPayload } from "@/features/rich-text";
import { trackClaudiaEvent, trackVoiceEvent } from "@/lib/analytics/domain";
import type { RightSidebarClaudiaViewContext } from "../contexts";
import { getAssistantErrorMessage, isThreadUnavailableError } from "../lib/ai-assistant-errors";
import { useClaudiaPromptHistory } from "../lib/claudia-prompt-history";
import type { AssistantSubmitPendingMode, AssistantTranscriptTimelineItem } from "../types";
import type { useAssistantRuntimeActivity } from "./use-assistant-runtime-activity";
import type { useOptimisticTranscriptExchange } from "./use-optimistic-transcript-exchange";

type RuntimeActivity = ReturnType<typeof useAssistantRuntimeActivity>;
type OptimisticExchange = ReturnType<typeof useOptimisticTranscriptExchange>;

export type ClaudiaViewContext = RightSidebarClaudiaViewContext;

export type ClaudiaFindingRef = {
  findingId: string;
  canonicalKey: string;
  ruleId?: string | null;
  missingAspects: string[];
};

export type PendingAskClaudiaIntent = {
  context: ClaudiaViewContext;
  trigger: "ask-claudia" | "editor-follow-up";
  sourceRunId?: string;
  initialPrompt: string;
  requestKey: number;
  startNewThread?: boolean;
  attachedSkillNames?: string[];
  // LAT-935: structured Quality Guard payload + the full-text prompt to fall back to when it
  // is absent. Mirrors `RightSidebarClaudiaAskIntent`.
  findingRefs?: ClaudiaFindingRef[];
  fallbackPrompt?: string;
  autoSubmit?: boolean;
} | null;

/**
 * The text to put in the composer (and send) for a pending intent: the short sentence when the
 * structured finding channel is carrying the payload, the full reference-line prompt when it is
 * not. LAT-935 — shrinking the visible prompt must never shrink what Claudia receives.
 */
export function resolvePendingIntentPrompt(intent: PendingAskClaudiaIntent): string {
  if (!intent) {
    return "";
  }
  if (intent.findingRefs && intent.findingRefs.length > 0) {
    return intent.initialPrompt;
  }
  return intent.fallbackPrompt ?? intent.initialPrompt;
}

export type AssistantSubmitMessageArgs = {
  projectId: Id<"projects">;
  prompt: string;
  threadId: string;
  voiceTranscriptionIds?: Id<"voiceTranscriptions">[];
  attachmentIds?: Id<"assistantAttachments">[];
  contextHandoff?:
    | {
        context: ReturnType<typeof normalizeAssistantViewContext>;
        trigger: "view-message" | "ask-claudia" | "editor-follow-up";
        sourceRunId?: string;
      }
    | undefined;
  // Turn-scoped skill attachment (e.g. the QG "Fix with Claudia" button). The backend
  // mutation already accepts this; the panel composer is the first FE caller. LAT-620.
  attachedSkillNames?: string[];
  // LAT-935: the structured Quality Guard channel. The server verifies each id against this
  // project and renders the finding into the prompt, so the visible text stays one sentence.
  qualityGuardFindingRefs?: {
    findingId: Id<"qualityGuardFindings">;
    canonicalKey: string;
    ruleId?: string | null;
    missingAspects: string[];
  }[];
};

/**
 * Widen the intent's plain-string finding ids to the branded Convex ids the mutation validator
 * expects. `build-claudia-fix-intent.ts` is deliberately free of Convex imports (it is a pure,
 * trivially-testable builder), so the branding is reapplied here, at the one boundary that
 * needs it. The cast is safe in the only direction that matters: the ids come from
 * `finding._id`, and the server treats them as untrusted regardless — it re-reads each row and
 * verifies the project before rendering anything.
 */
function toQualityGuardFindingRefWire(refs: ClaudiaFindingRef[]) {
  return refs.map((ref) => ({
    findingId: ref.findingId as Id<"qualityGuardFindings">,
    canonicalKey: ref.canonicalKey,
    ruleId: ref.ruleId ?? null,
    missingAspects: ref.missingAspects,
  }));
}

/**
 * Where a send came from. A `"composer"` send is the person pressing Send on their own draft and
 * carries everything staged beside it; a `"quick-reply"` send is a Next steps row, which carries
 * the letter sentence and nothing else. The composer is left exactly as it was — staged
 * attachments, pending voice transcripts, the typed draft and any pending ask-Claudia intent are
 * still there to send with whatever the person types next.
 */
export type ClaudiaSendOrigin = "composer" | "quick-reply";

export type ClaudiaSendOptions = { origin?: ClaudiaSendOrigin };

/**
 * What a send takes from the composer besides the text. A quick reply takes none of it, so the
 * clean-up each field would trigger — resetting the attachment draft, dropping the used voice
 * transcripts, clearing the intent — has nothing to act on and does not run.
 */
type CarriedComposerState = {
  voiceTranscriptionIds: Id<"voiceTranscriptions">[];
  attachmentIds: Id<"assistantAttachments">[];
  optimisticAttachments: NonNullable<OptimisticExchange["exchange"]>["attachments"] | undefined;
  intent: PendingAskClaudiaIntent;
};

const CARRIES_NOTHING: CarriedComposerState = {
  voiceTranscriptionIds: [],
  attachmentIds: [],
  optimisticAttachments: undefined,
  intent: null,
};

export type AssistantSubmitMessage = (args: AssistantSubmitMessageArgs) => Promise<{
  promptMessageId: string;
  runId?: string | null;
  threadId: string;
  // RC-8: the backend tells us whether the message started a run or was absorbed as steering into
  // the run already in flight. Dropping it is what made mid-run messages vanish silently.
  pendingMode?: AssistantSubmitPendingMode;
  queuedInputId?: string;
}>;

interface UseClaudiaComposerSubmissionArgs {
  projectId: Id<"projects">;
  threadId: string | null;
  surface: "full_screen" | "right_panel";
  promptHistoryUserId: string | null;
  isSubmitting: boolean;
  canSubmitPrompt: boolean;
  attachmentIds?: Id<"assistantAttachments">[];
  optimisticAttachments?: NonNullable<OptimisticExchange["exchange"]>["attachments"];
  hasAttachments?: boolean;
  isUploadingAttachments?: boolean;
  resetAttachments?: () => void;
  /**
   * Raised around the submit call, so the draft the composer is holding survives being abandoned
   * mid-send: the attachment rows are linked to the message inside that transaction, and until it
   * answers they still look like drafts nobody will send.
   */
  setAttachmentsSubmissionInFlight?: (inFlight: boolean) => void;
  currentViewContext?: ClaudiaViewContext | null;
  pendingAskClaudiaIntent?: PendingAskClaudiaIntent;
  clearAskClaudiaIntent?: () => void;
  timelineItems: AssistantTranscriptTimelineItem[];
  runtimeActivity: RuntimeActivity;
  sendMessage: AssistantSubmitMessage;
  startExchange: OptimisticExchange["startExchange"];
  resolveExchange: OptimisticExchange["resolveExchange"];
  clearExchange: OptimisticExchange["clearExchange"];
  setIsSubmitting: (value: boolean) => void;
  setError: (value: string | null) => void;
  setCurrentRunId: (value: string | null) => void;
  setLatestPromptMessageId: (value: string | null) => void;
  onThreadUnavailable?: (cause: unknown) => void;
  onMessageSubmitted?: (result: Awaited<ReturnType<AssistantSubmitMessage>>) => void;
}

function getNextTranscriptSourceOrder(items: AssistantTranscriptTimelineItem[]) {
  const highestSourceOrder = items.reduce((maxOrder, item) => {
    if (item.kind !== "message") {
      return maxOrder;
    }
    return Math.max(maxOrder, item.sourceOrder);
  }, -1);
  return highestSourceOrder + 1;
}

export function normalizeAssistantViewContext(context: ClaudiaViewContext) {
  if (context.kind === "entity-tab") {
    return {
      ...context,
      tabKey: context.tabKey ?? undefined,
      tabLabel: context.tabLabel ?? undefined,
    };
  }

  if (context.kind === "entity-section") {
    return {
      ...context,
      guidance: context.guidance ?? undefined,
      expectedMentionTypes: context.expectedMentionTypes ?? undefined,
    };
  }

  if (context.kind === "folder" || context.kind === "collection") {
    return {
      ...context,
      parentFolderLabel: context.parentFolderLabel ?? undefined,
      parentFolderRoutePath: context.parentFolderRoutePath ?? undefined,
      createTargetLabel: context.createTargetLabel ?? undefined,
      activeFilters: context.activeFilters ?? undefined,
      visibleItems: context.visibleItems?.map((item) => ({
        ...item,
        entitySlug: item.entitySlug ?? undefined,
      })),
      hiddenMatchingItems: context.hiddenMatchingItems?.map((item) => ({
        ...item,
        entitySlug: item.entitySlug ?? undefined,
      })),
      allScopedItems: context.allScopedItems?.map((item) => ({
        ...item,
        entitySlug: item.entitySlug ?? undefined,
      })),
    };
  }

  return {
    ...context,
    label: context.label ?? undefined,
  };
}

export function useClaudiaComposerSubmission({
  projectId,
  threadId,
  surface,
  promptHistoryUserId,
  isSubmitting,
  canSubmitPrompt,
  attachmentIds = [],
  optimisticAttachments,
  hasAttachments = false,
  isUploadingAttachments = false,
  resetAttachments,
  setAttachmentsSubmissionInFlight,
  currentViewContext = null,
  pendingAskClaudiaIntent = null,
  clearAskClaudiaIntent,
  timelineItems,
  runtimeActivity,
  sendMessage,
  startExchange,
  resolveExchange,
  clearExchange,
  setIsSubmitting,
  setError,
  setCurrentRunId,
  setLatestPromptMessageId,
  onThreadUnavailable,
  onMessageSubmitted,
}: UseClaudiaComposerSubmissionArgs) {
  const [pendingVoiceTranscriptionIds, setPendingVoiceTranscriptionIds] = useState<
    Id<"voiceTranscriptions">[]
  >([]);
  const previousThreadIdRef = useRef<string | null>(threadId);
  const { entries: promptHistoryEntries, recordPrompt } = useClaudiaPromptHistory({
    projectId,
    userId: promptHistoryUserId,
  });

  /**
   * LAT-990: this used to clear the whole submission surface on a thread change — the prompt id,
   * the run id, the expanded sections, the optimistic exchange — as a second mechanism beside the
   * one each surface already had. In the full-screen view it never ran: a switch drops the page
   * into its loading branch, which unmounts the composer, and `previousThreadIdRef` re-initialises
   * to the new thread on the remount. Two mechanisms for one reset is how nobody noticed the other
   * one was dead, so what is left here is the state only this hook owns. Everything else is scoped
   * to its thread by the surface that owns it (`lib/thread-scoped-state.ts`), and the attachment
   * draft clears itself on the same edge (`use-claudia-attachments.ts`).
   */
  useEffect(() => {
    if (previousThreadIdRef.current === threadId) {
      return;
    }
    previousThreadIdRef.current = threadId;
    setPendingVoiceTranscriptionIds([]);
  }, [threadId]);

  useEffect(() => {
    const terminalRunStatus = runtimeActivity.runtimeRun?.status ?? null;
    const hasTerminalResolution =
      terminalRunStatus === "COMPLETED" ||
      terminalRunStatus === "COMPLETED_WITH_WARNINGS" ||
      terminalRunStatus === "CANCELLED";

    if (runtimeActivity.promptExecutionStatus?.phase === "FAILED") {
      setCurrentRunId(null);
      setError(
        hasTerminalResolution
          ? null
          : (runtimeActivity.promptExecutionStatus.lastFailure ??
              "Assistant response failed. Please try again.")
      );
      return;
    }

    if (runtimeActivity.promptExecutionStatus?.phase === "STOPPED") {
      setCurrentRunId(null);
    }
  }, [
    runtimeActivity.promptExecutionStatus,
    runtimeActivity.runtimeRun?.status,
    setCurrentRunId,
    setError,
  ]);

  const handleVoiceTranscriptionCreated = useCallback(
    (transcriptionId: Id<"voiceTranscriptions">) => {
      setPendingVoiceTranscriptionIds((current) =>
        current.includes(transcriptionId) ? current : [...current, transcriptionId]
      );
    },
    []
  );

  const handleSend = useCallback(
    async ({ markdown }: MessageRichTextSubmitPayload, options?: ClaudiaSendOptions) => {
      const origin: ClaudiaSendOrigin = options?.origin ?? "composer";
      const prompt = markdown.trim();
      // Staged attachments are what makes a wordless send meaningful, and a quick reply carries
      // none, so its text is the whole message.
      if ((!prompt && (origin === "quick-reply" || !hasAttachments)) || !threadId) {
        return false;
      }
      if (isSubmitting || !canSubmitPrompt) {
        return false;
      }
      // Kept for a quick reply too: both composers already withhold the offer while an upload is
      // in flight, so this refuses nothing a row would let through, and one send path with one
      // set of preconditions cannot drift from a second.
      if (isUploadingAttachments) {
        return false;
      }

      const carried: CarriedComposerState =
        origin === "quick-reply"
          ? CARRIES_NOTHING
          : {
              voiceTranscriptionIds: pendingVoiceTranscriptionIds.slice(),
              attachmentIds: attachmentIds.slice(),
              optimisticAttachments,
              intent: pendingAskClaudiaIntent,
            };
      const { voiceTranscriptionIds } = carried;
      setIsSubmitting(true);
      setError(null);
      setCurrentRunId(null);
      setLatestPromptMessageId(null);
      const startedExchange = startExchange({
        prompt,
        attachments: carried.optimisticAttachments,
        sourceOrder: getNextTranscriptSourceOrder(timelineItems),
        threadId,
      });
      const carriesAttachments = carried.attachmentIds.length > 0;
      if (carriesAttachments) {
        setAttachmentsSubmissionInFlight?.(true);
      }
      try {
        const result = await sendMessage({
          projectId,
          prompt,
          threadId,
          voiceTranscriptionIds:
            voiceTranscriptionIds.length > 0 ? voiceTranscriptionIds : undefined,
          attachmentIds: carried.attachmentIds.length > 0 ? carried.attachmentIds : undefined,
          attachedSkillNames:
            carried.intent?.attachedSkillNames && carried.intent.attachedSkillNames.length > 0
              ? carried.intent.attachedSkillNames
              : undefined,
          qualityGuardFindingRefs:
            carried.intent?.findingRefs && carried.intent.findingRefs.length > 0
              ? toQualityGuardFindingRefWire(carried.intent.findingRefs)
              : undefined,
          contextHandoff: carried.intent
            ? {
                context: normalizeAssistantViewContext(carried.intent.context),
                trigger: carried.intent.trigger,
                sourceRunId: carried.intent.sourceRunId,
              }
            : currentViewContext
              ? {
                  context: normalizeAssistantViewContext(currentViewContext),
                  trigger: "view-message",
                }
              : undefined,
        });
        resolveExchange({
          localExchangeId: startedExchange.localExchangeId,
          promptMessageId: result.promptMessageId,
          runId: result.runId ?? null,
          pendingMode: result.pendingMode ?? null,
          queuedInputId: result.queuedInputId ?? null,
        });
        // The prompt history is the list of things this person typed; a letter sentence the row
        // wrote is not one of them.
        if (origin === "composer") {
          recordPrompt(prompt);
        }
        if (voiceTranscriptionIds.length > 0) {
          setPendingVoiceTranscriptionIds((current) =>
            current.filter((id) => !voiceTranscriptionIds.includes(id))
          );
        }
        if (carriesAttachments) {
          resetAttachments?.();
        }
        if (carried.intent) {
          clearAskClaudiaIntent?.();
        }
        trackClaudiaEvent("claudia_message_sent", {
          surface,
          origin,
          project_id: projectId,
          thread_id: result.threadId,
          has_voice_input: voiceTranscriptionIds.length > 0,
          has_context_handoff: Boolean(carried.intent || currentViewContext),
          context_kind: carried.intent?.context.kind ?? currentViewContext?.kind,
        });
        if (voiceTranscriptionIds.length > 0) {
          trackVoiceEvent("voice_transcript_used", {
            source: "claudia-composer",
            surface,
            project_id: projectId,
            thread_id: result.threadId,
            target_action: "claudia_message_sent",
          });
        }
        setLatestPromptMessageId(result.promptMessageId);
        setCurrentRunId(result.runId ?? null);
        onMessageSubmitted?.(result);
        return true;
      } catch (cause: unknown) {
        clearExchange(startedExchange.localExchangeId);
        setError(getAssistantErrorMessage(cause, "Failed to send prompt"));
        if (isThreadUnavailableError(cause)) {
          onThreadUnavailable?.(cause);
        }
        return false;
      } finally {
        if (carriesAttachments) {
          setAttachmentsSubmissionInFlight?.(false);
        }
        setIsSubmitting(false);
      }
    },
    [
      canSubmitPrompt,
      attachmentIds,
      clearAskClaudiaIntent,
      clearExchange,
      currentViewContext,
      hasAttachments,
      isUploadingAttachments,
      isSubmitting,
      onMessageSubmitted,
      onThreadUnavailable,
      optimisticAttachments,
      pendingAskClaudiaIntent,
      pendingVoiceTranscriptionIds,
      projectId,
      recordPrompt,
      resolveExchange,
      resetAttachments,
      sendMessage,
      setAttachmentsSubmissionInFlight,
      setCurrentRunId,
      setError,
      setIsSubmitting,
      setLatestPromptMessageId,
      startExchange,
      surface,
      threadId,
      timelineItems,
    ]
  );

  return {
    handleSend,
    handleVoiceTranscriptionCreated,
    promptHistoryEntries,
  };
}
